import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Amplify } from 'aws-amplify';
import {
  AuthUser,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  signIn,
  SignInInput,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth';

import { TAuthState } from '@/shared/redux/api/auth/auth-api.types';
import { clearCachedToken } from '@/shared/redux/api/utils/auth';
import { LoadingStateEnum } from '@/shared/types/common.types';
import { clearStorageKeepingPreferences } from '@/shared/utils/storage/storage-keys';

export const login = createAsyncThunk<
  SignInOutput,
  SignInInput,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const { isSignedIn, nextStep } = await signIn(credentials);
    return { isSignedIn, nextStep };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const fetchCurrentUser = createAsyncThunk<
  {
    user: AuthUser;
    attributes: FetchUserAttributesOutput;
    mfaEnabled: boolean;
    isDeveloperClaim: string | null;
  },
  void,
  { rejectValue: string }
>('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    const mfaPreference = await fetchMFAPreference();
    const mfaEnabled = (mfaPreference?.enabled ?? []).length > 0;

    // Read from the ID token rather than fetchUserAttributes: the back-office
    // gates on the token claim API Gateway validated, and the pool attribute
    // changes the moment it is backfilled while the token keeps the old value
    // until it refreshes. Trusting the attribute renders the developer UI for a
    // user whose every request then 403s.
    const { tokens } = await fetchAuthSession();
    const rawClaim = tokens?.idToken?.payload?.['custom:isDeveloper'];

    return {
      user,
      attributes,
      mfaEnabled,
      isDeveloperClaim: typeof rawClaim === 'string' ? rawClaim : null,
    };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

const OAUTH_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';
const LEGACY_HOSTED_UI_KEY = 'amplify-signin-with-hostedUI';

const hasHostedUiSession = (clientId?: string): boolean => {
  const marker = clientId
    ? localStorage.getItem(`${OAUTH_STORAGE_PREFIX}.${clientId}.oauthSignIn`)
    : null;

  return (
    marker?.split(',')[0] === 'true' ||
    localStorage.getItem(LEGACY_HOSTED_UI_KEY) === 'true'
  );
};

export const logout = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const cognito = Amplify.getConfig().Auth?.Cognito;
      // signOut redirects to the Cognito logout page under exactly these
      // conditions, and that redirect is what ends the identity provider
      // session as well. Reloading afterwards would cancel it.
      const redirectsToLogoutPage =
        Boolean(cognito?.loginWith?.oauth?.domain) &&
        hasHostedUiSession(cognito?.userPoolClientId);

      clearCachedToken();
      await signOut({ global: true });
      clearStorageKeepingPreferences();

      if (!redirectsToLogoutPage) {
        window.location.reload();
      }
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

const initialState: TAuthState = {
  isAuthenticated: false,
  user: null,
  isDeveloperClaim: null,
  error: null,
  status: LoadingStateEnum.IDLE,
  signInStep: null,
  backofficeTenantIdToken: null,
  maintenanceAlertOpen: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setBackofficeTenantIdToken: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.backofficeTenantIdToken = action.payload;
    },
    setMaintenanceAlertOpen: (state, action: PayloadAction<boolean>) => {
      state.maintenanceAlertOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<SignInOutput>) => {
          state.isAuthenticated = action.payload.isSignedIn;
          state.signInStep = action.payload.nextStep?.signInStep;
        },
      )
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload ?? 'Login failed';
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.status = LoadingStateEnum.LOADING;
      })
      .addCase(
        fetchCurrentUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            user: AuthUser;
            attributes: FetchUserAttributesOutput;
            mfaEnabled: boolean;
            isDeveloperClaim: string | null;
          }>,
        ) => {
          state.status = LoadingStateEnum.DONE;
          state.isDeveloperClaim = action.payload.isDeveloperClaim;
          state.user = {
            ...action.payload.user,
            // Backoffice and Console authenticate against separate Cognito
            // pools, so only one of these attributes is ever present: super
            // users carry `custom:superUserId`, tenant users `custom:userId`.
            userId:
              action.payload.attributes['custom:superUserId'] ??
              action.payload.attributes['custom:userId']!,
            attributes: action.payload.attributes,
            mfaEnabled: action.payload.mfaEnabled,
          };
        },
      )
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = LoadingStateEnum.DONE;
        state.user = null;
        state.isDeveloperClaim = null;
        state.error = action.payload ?? 'Failed to fetch user';
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export default authSlice.reducer;

export const selectUser = (state: { auth: TAuthState }) => state.auth.user;
export const selectAuthStatus = (state: { auth: TAuthState }) =>
  state.auth.status;

// The claim is a Cognito String, so "0" and "false" arrive as truthy strings and
// only an explicit affirmative counts. Both "1" and "true" are accepted because
// the pool genuinely holds both: IDEaaS was backfilled with "1", the feature flag
// catalog with "true". The back-office parses the same two values.
const AFFIRMATIVE_IS_DEVELOPER_VALUES = ['1', 'true'];

export const selectIsDevUser = (state: { auth: TAuthState }) =>
  AFFIRMATIVE_IS_DEVELOPER_VALUES.includes(
    (state.auth.isDeveloperClaim ?? '').trim().toLowerCase(),
  );
export const selectBackofficeTenantIdToken = (state: { auth: TAuthState }) =>
  state.auth.backofficeTenantIdToken;
export const selectMaintenanceAlertOpen = (state: { auth: TAuthState }) =>
  state.auth.maintenanceAlertOpen;

export const { setBackofficeTenantIdToken, setMaintenanceAlertOpen } =
  authSlice.actions;
